/* eslint-disable @typescript-eslint/no-explicit-any */
import { Action, History, Location, createBrowserHistory } from 'history';
import _ from 'lodash';

import * as historyActions from 'actions/HistoryActions';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { logger } from '@tock/logging';

/**
 * Creates a history object that contains a superset of fields/methods
 * from react-router's createBrowserHistory.
 */
export default class TockHistory {
  private _store: any;
  browserHistory: History;
  unlisten: any;

  constructor(props = {}) {
    this.browserHistory = createBrowserHistory(props);

    this.unlisten = this.browserHistory.listen((location: Location, action: Action) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.info('Listen in tock history', action, location);
      }
      switch (action) {
        case 'PUSH': {
          this._store.dispatch(historyActions.push(location));
          return true;
        }
        case 'REPLACE': {
          this._store.dispatch(historyActions.replace(location));
          return true;
        }
        case 'POP': {
          // note: this will not fire when in an iframe(aka Chase), see clientBootstrap message handler
          this._store.dispatch(historyActions.back(1));
          return true;
        }
        default: {
          assertNever(action);
        }
      }
    });
  }

  set store(value: any) {
    this._store = value;
    /**
     * What is this doing? We store our own history stack of Tock
     * pages so that we can investigate it for certain purposes.
     * That tracking mostly happens in a listener on browser history.
     * However, just using that listener would always miss the first
     * page loaded! So additionally we push that page here, unless it
     * would simply duplicate the previous entry.
     */
    const locationHistory = this._store.getState().history.locationHistory;
    const location = this.browserHistory.location;
    if (locationHistory.length > 0) {
      if (locationHistory[0].pathname !== location.pathname) {
        this.fillInInitialPageToTockHistory(location, 'push');
      }
    } else {
      this.fillInInitialPageToTockHistory(location, 'replace');
    }
  }

  fillInInitialPageToTockHistory = (location: Location<unknown>, method: 'push' | 'replace') => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.info('Push location to history before listening to Tock History');
    }
    this._store.dispatch(historyActions.push(location));
  };

  /*
   * A method to navigate back but only using the history of Tock pages
   * (i.e. to avoid navigating back to a non-Tock page). If no Tock pages
   * are recorded in the history stack, this will replace the current location
   * with "/".
   */
  appBack = () => {
    const locationHistory = this._store.getState().history.locationHistory;
    if (locationHistory && locationHistory.length > 1) {
      this.browserHistory.goBack();
    } else {
      this.browserHistory.replace('/');
    }
  };

  /*
   * A method to navigate by popping n slash-delimited elements off of
   * a path. E.g. when at /foo/bar, navigationPop(1) will navigate to /foo.
   * When at /foo/bar/baz, navigationPop(2) will also navigate to /foo.
   *
   * This will navigate via a history.push meaning the previous location
   * will remain on the history stack.
   */
  pathPopPush = (n: number, maintainSearch = false, maintainHash = false) => {
    if (!this._store) {
      return;
    }
    const locationHistory = this._store.getState().history.locationHistory;
    if (locationHistory && locationHistory.length > 0) {
      const modifiedLocation = this._getModifiedLocation(
        locationHistory[0],
        n,
        maintainSearch,
        maintainHash
      );

      this.browserHistory.push(modifiedLocation);
    } else {
      // No history
      return;
    }
  };

  /*
   * A method to navigate by popping n slash-delimited elements off of
   * a path. E.g. when at /foo/bar, navigationPop(1) will navigate to /foo.
   * When at /foo/bar/baz, navigationPop(2) will also navigate to /foo.
   *
   * This will navigate via a history.replace meaning the previous location
   * will be removed from the history stack.
   */
  pathPopReplace = (n: number, maintainSearch = false, maintainHash = false) => {
    if (!this._store) {
      return;
    }
    const locationHistory = this._store.getState().history.locationHistory;
    if (locationHistory && locationHistory.length > 0) {
      const modifiedLocation = this._getModifiedLocation(
        locationHistory[0],
        n,
        maintainSearch,
        maintainHash
      );

      this.browserHistory.replace(modifiedLocation);
    } else {
      // No history
      return;
    }
  };

  _getModifiedLocation = (
    location: Location,
    n: number,
    maintainSearch: boolean,
    maintainHash: boolean
  ): Location => {
    const modifiedLocation = _.cloneDeep(location);
    const splitPathname = _.split(modifiedLocation.pathname, '/');

    if (splitPathname.length > n) {
      modifiedLocation.pathname = _.join(_.dropRight(splitPathname, n), '/');
    } else {
      // Tried to pop too many elements.
      modifiedLocation.pathname = '/';
    }

    if (!maintainSearch) {
      modifiedLocation.search = '';
    }

    if (!maintainHash) {
      modifiedLocation.hash = '';
    }

    return modifiedLocation;
  };

  push = (...args: any[]) => {
    logger.debug('push called in tock history', args);
    return (this.browserHistory as any).push(...args);
  };
  replace = (...args: any[]) => {
    logger.debug('replace called in tock history', args);
    return (this.browserHistory as any).replace(...args);
  };
  go = (...args: any[]) => {
    logger.debug('go called in tock history', args);
    return (this.browserHistory as any).go(...args);
  };
  goBack = (...args: any[]) => {
    logger.debug('goBack called in tock history', args);
    return (this.browserHistory as any).goBack(...args);
  };
  goBackFromMessage = (...args: any[]) => {
    logger.debug('goBack from messages called in tock history', args);
    // We remove the history, then let the parent route replace the current route.
    this._store.dispatch(historyActions.back(1));
    return (this.browserHistory as any).replace(...args);
  };
  goForward = (...args: any[]) => {
    logger.debug('goforward called in tock history', args);
    return (this.browserHistory as any).goForward(...args);
  };
  block = (...args: any[]) => {
    logger.debug('block called in tock history', args);
    return (this.browserHistory as any).block(...args);
  };
  listen = (...args: any[]) => {
    return (this.browserHistory as any).listen(...args);
  };
  createHref = (...args: any[]) => {
    return (this.browserHistory as any).createHref(...args);
  };

  get length() {
    return this.browserHistory.length;
  }

  get action() {
    return this.browserHistory.action;
  }

  get location() {
    return this.browserHistory.location;
  }
}
